import type React from "react";
import { useEffect, useMemo, useRef } from "react";

import { useTranslations } from "@ui/providers/useTranslations";
import { getDaysInMonth } from "@ui/utils/date";
import { formatDate } from "@ui/utils/formatDate/formatDate";

import type { SelectOptionType } from "@jti/fe-common";
import { DOB_INPUT_DATE, useDirection } from "@jti/fe-common";

import { getNewDaysArray } from "../DEPRECATED_CustomSelect";
import type { FieldErrors } from "../Form";
import { Controller, useFormContext } from "../Form";
import { TextInput } from "../Inputs";

import styles from "./DateInputs.module.scss";

type DateInputsProps = {
  fieldsPrefix: string;
  fieldName: string;
  yearsRange?: SelectOptionType[];
  isDateChangeRestricted?: boolean;
  isReversed?: boolean;
  required?: boolean;
};

const minPossibleYear = 1900;
const DOB_INVALID_ERROR_TYPE = "dob-invalid" as const;

type DobSubFields = { dobDay: string; dobMonth: string; dobYear: string };

const isDayValid = (day: string, maxDay: number) => Number(day) >= 1 && Number(day) <= maxDay;
const isMonthValid = (month: string) => Number(month) >= 1 && Number(month) <= 12;
const isYearValid = (year: string, maxYear: number) => Number(year) >= minPossibleYear && Number(year) <= maxYear;

export const DateInputs = ({
  fieldsPrefix,
  fieldName,
  yearsRange,
  isDateChangeRestricted,
  isReversed,
  required,
}: DateInputsProps) => {
  const {
    watch,
    setValue,
    setError,
    clearErrors,
    register,
    trigger,
    formState: { errors, dirtyFields },
    control,
    getValues,
    resetField,
  } = useFormContext();
  const { __ } = useTranslations();
  const dir = useDirection();

  const dayInput = useRef<HTMLInputElement | null>(null);
  const monthInput = useRef<HTMLInputElement | null>(null);
  const yearInput = useRef<HTMLInputElement | null>(null);
  const previousInputValuesRef = useRef<Record<string, string>>({});

  const dobDay = watch(`${fieldsPrefix}.dobDay`);
  const dobMonth = watch(`${fieldsPrefix}.dobMonth`);
  const dobYear = watch(`${fieldsPrefix}.dobYear`);
  const previousMonthYearRef = useRef({ dobMonth, dobYear });

  const preparedDobError = errors[fieldName];
  const dobErrors = errors[fieldsPrefix] as FieldErrors<DobSubFields> | undefined;
  const dayError = dobErrors?.dobDay;
  const monthError = dobErrors?.dobMonth;
  const yearError = dobErrors?.dobYear;

  const daysInSelectedMonth = useMemo(() => getNewDaysArray(getDaysInMonth(dobMonth, dobYear)), [dobMonth, dobYear]);
  const maxDayInMonth = Number(daysInSelectedMonth.at(-1)?.value);
  const maxAllowedYear = useMemo(
    () => (yearsRange?.[0]?.value != null ? Number(yearsRange[0].value) : new Date().getFullYear()),
    [yearsRange]
  );
  const dayErrorMessage = dayError?.message;
  const monthErrorMessage = monthError?.message;
  const yearErrorMessage = yearError?.message;

  const subFieldNames = useMemo<[string, string, string]>(
    () => [`${fieldsPrefix}.dobDay`, `${fieldsPrefix}.dobMonth`, `${fieldsPrefix}.dobYear`],
    [fieldsPrefix]
  );

  const handleAgeInputKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
    previousInputValuesRef.current[event.currentTarget.name] = event.currentTarget.value;
  };

  const handleAgeInputKeyUp: React.KeyboardEventHandler<HTMLInputElement> = async (event) => {
    const { value: inputValue, name: inputName, maxLength } = event.currentTarget;
    const previousInputValue = previousInputValuesRef.current[inputName] ?? "";
    previousInputValuesRef.current[inputName] = inputValue;
    const valueLength = inputValue.length;

    if (Number(inputValue) < 0 || isNaN(Number(inputValue))) {
      setValue(inputName, "");
      trigger(inputName);
      return;
    }

    const inputsList = isReversed
      ? [yearInput.current, monthInput.current, dayInput.current]
      : [dayInput.current, monthInput.current, yearInput.current];
    const activeInputIndex = inputsList.indexOf(event.currentTarget);

    const shouldFocusPreviousInput =
      !valueLength &&
      event.key === "Backspace" &&
      activeInputIndex !== 0 &&
      (!isReversed || previousInputValue.length === 0);

    if (shouldFocusPreviousInput) {
      inputsList[activeInputIndex - 1]?.focus({ preventScroll: true });
      event.preventDefault();
    }

    if (valueLength >= maxLength && inputValue === inputsList[activeInputIndex]?.value) {
      const isFieldValid = await trigger(inputName);

      if (isFieldValid && activeInputIndex !== inputsList.length - 1) {
        inputsList[activeInputIndex + 1]?.focus({ preventScroll: true });
        event.preventDefault();
      }
    }
  };

  useEffect(() => {
    const hasMonthOrYearChanged =
      previousMonthYearRef.current.dobMonth !== dobMonth || previousMonthYearRef.current.dobYear !== dobYear;

    previousMonthYearRef.current = { dobMonth, dobYear };
    if (!hasMonthOrYearChanged || !dobDay) {
      return;
    }

    void trigger(`${fieldsPrefix}.dobDay`);
  }, [dobMonth, dobYear, dobDay, fieldsPrefix, trigger]);

  useEffect(() => {
    const isDobHasValue = dobDay && dobMonth && dobYear;
    const isDateInvalid = isNaN(dobDay) || isNaN(dobMonth) || isNaN(dobYear);
    const isFullYear = dobYear?.length === 4;
    const areAllSubFieldsValid =
      isMonthValid(dobMonth) && isDayValid(dobDay, maxDayInMonth) && isYearValid(dobYear, maxAllowedYear);
    const isFormDateInvalid = Boolean(dayErrorMessage || monthErrorMessage || yearErrorMessage);

    if (isDobHasValue && !isDateInvalid && isFullYear && areAllSubFieldsValid) {
      const date = new Date(dobYear, dobMonth - 1, dobDay);
      const formattedDate = formatDate(date, DOB_INPUT_DATE);

      if (preparedDobError?.type === DOB_INVALID_ERROR_TYPE) {
        clearErrors(fieldName);
      }

      setValue(fieldName, formattedDate);

      return;
    }

    setValue(fieldName, "");

    const hasSubFieldError =
      Boolean(dobDay || dobMonth || dobYear) &&
      (isFormDateInvalid || (isDobHasValue && !isDateInvalid && isFullYear && !areAllSubFieldsValid));

    if (hasSubFieldError) {
      const isExternalError = preparedDobError != null && preparedDobError.type !== DOB_INVALID_ERROR_TYPE;

      if (!isExternalError) {
        const subFieldMessage =
          dayErrorMessage ??
          monthErrorMessage ??
          yearErrorMessage ??
          __("auth.age_verification.method_fields.dob.error_invalid");

        if (preparedDobError?.message !== subFieldMessage) {
          setError(fieldName, {
            type: DOB_INVALID_ERROR_TYPE,
            message: subFieldMessage,
          });
        }
      }
    } else if (preparedDobError?.type === DOB_INVALID_ERROR_TYPE) {
      // Sub-fields all empty — replace our error with parent's (e.g. "required")
      trigger(fieldName);
    }
  }, [
    dobDay,
    dobMonth,
    dobYear,
    fieldName,
    setValue,
    setError,
    clearErrors,
    trigger,
    preparedDobError,
    dirtyFields,
    fieldsPrefix,
    maxDayInMonth,
    maxAllowedYear,
    dayErrorMessage,
    monthErrorMessage,
    yearErrorMessage,
    __,
  ]);

  useEffect(() => {
    for (const field of subFieldNames) {
      resetField(field, {
        keepError: true,
        keepDirty: false,
        keepTouched: false,
        defaultValue: getValues(field),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    register(fieldName);
  }, [fieldName, register]);

  const dobInputs = [
    <Controller
      control={control}
      name={`${fieldsPrefix}.dobDay`}
      key={`${fieldsPrefix}.dobDay`}
      rules={{
        validate: {
          isDayValid: (day) =>
            isDayValid(day, maxDayInMonth) || __("auth.age_verification.method_fields.dob_day.error_invalid"),
        },
      }}
      render={({ field }) => (
        <TextInput
          {...field}
          id="dobDayInput"
          aria-label={__("auth.age_verification.method_fields.dob_day.select_placeholder")}
          containerClassName={styles.input}
          placeholder={__("auth.age_verification.method_fields.dob_day.select_placeholder")}
          data-testid="dobDayInput"
          disabled={isDateChangeRestricted}
          ref={(dayElement) => {
            field.ref(dayElement);
            dayInput.current = dayElement;
          }}
          type="tel"
          maxLength={2}
          autoComplete="bday-day"
          isError={Boolean(dayError ?? preparedDobError)}
          onKeyDown={handleAgeInputKeyDown}
          onKeyUp={handleAgeInputKeyUp}
          required={required}
          dir={dir}
        />
      )}
    />,
    <Controller
      control={control}
      name={`${fieldsPrefix}.dobMonth`}
      key={`${fieldsPrefix}.dobMonth`}
      rules={{
        validate: {
          isMonthValid: (month) =>
            isMonthValid(month) || __("auth.age_verification.method_fields.dob_month.error_invalid"),
        },
      }}
      render={({ field }) => (
        <TextInput
          {...field}
          id="dobMonthInput"
          aria-label={__("auth.age_verification.method_fields.dob_month.select_placeholder")}
          containerClassName={styles.input}
          placeholder={__("auth.age_verification.method_fields.dob_month.select_placeholder")}
          data-testid="dobMonthInput"
          disabled={isDateChangeRestricted}
          ref={(monthElement) => {
            field.ref(monthElement);
            monthInput.current = monthElement;
          }}
          type="tel"
          maxLength={2}
          autoComplete="bday-month"
          isError={Boolean(monthError ?? preparedDobError)}
          onKeyDown={handleAgeInputKeyDown}
          onKeyUp={handleAgeInputKeyUp}
          required={required}
          dir={dir}
        />
      )}
    />,
    <Controller
      control={control}
      name={`${fieldsPrefix}.dobYear`}
      key={`${fieldsPrefix}.dobYear`}
      rules={{
        validate: {
          isYearValid: (year) =>
            isYearValid(year, maxAllowedYear) || __("auth.age_verification.method_fields.dob_year.error_invalid"),
        },
      }}
      render={({ field }) => (
        <TextInput
          {...field}
          containerClassName={styles.input}
          id="dobYearInput"
          aria-label={__("auth.age_verification.method_fields.dob_year.select_placeholder")}
          placeholder={__("auth.age_verification.method_fields.dob_year.select_placeholder")}
          data-testid="dobYearInput"
          disabled={isDateChangeRestricted}
          ref={(yearElement) => {
            field.ref(yearElement);
            yearInput.current = yearElement;
          }}
          type="tel"
          maxLength={4}
          autoComplete="bday-year"
          isError={Boolean(yearError ?? preparedDobError)}
          onKeyDown={handleAgeInputKeyDown}
          onKeyUp={handleAgeInputKeyUp}
          required={required}
          dir={dir}
        />
      )}
    />,
  ];

  return <div className={styles.dateInputsContainer}>{isReversed ? dobInputs.toReversed() : dobInputs}</div>;
};
